import type { ApiClient } from '@classgo/app-kit';
import type {
  CreateEvaluationInput,
  EvaluationList,
  EvaluationScoresInput,
  EvaluationView,
  GradesView,
  PeriodGradeAdjustmentInput,
  PublishGradesInput,
  PublishGradesResult,
  ReopenGradesInput,
  ReopenGradesResult,
  UpdateEvaluationInput,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

import type { GradesCell } from './types';

const cellQuery = (cell: GradesCell) =>
  `?${new URLSearchParams({ groupId: cell.groupId, subjectId: cell.subjectId, periodId: cell.periodId }).toString()}`;

/**
 * The grade book's write side (#115 over the API of #106): the evaluations of a cell (group ×
 * subject × period), the scores of one evaluation saved in one call (idempotent per student),
 * the override of a period grade with its observation, and the publication and reopening of a
 * subject or of the whole group. Every write answers with what the screen shows next: the
 * evaluation, or the cell's grade book with the period grades recomputed.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const gradebookApi = (api: ApiClient) => ({
  createEvaluation: (body: CreateEvaluationInput) =>
    api.request<EvaluationView>('/evaluations', { method: 'POST', body }),
  updateEvaluation: (id: string, body: UpdateEvaluationInput) =>
    api.request<EvaluationView>(`/evaluations/${id}`, { method: 'PATCH', body }),
  deleteEvaluation: (id: string) => api.request<void>(`/evaluations/${id}`, { method: 'DELETE' }),
  putScores: (id: string, body: EvaluationScoresInput) =>
    api.request<GradesView>(`/evaluations/${id}/scores`, { method: 'PUT', body }),
  adjust: (body: PeriodGradeAdjustmentInput) =>
    api.request<GradesView>('/school/period-grades', { method: 'PATCH', body }),
  publish: (body: PublishGradesInput) =>
    api.request<PublishGradesResult>('/school/period-grades/publish', { method: 'POST', body }),
  reopen: (body: ReopenGradesInput) =>
    api.request<ReopenGradesResult>('/school/period-grades/reopen', { method: 'POST', body }),
});

/**
 * The grade book's reads: the evaluations of a cell with how many students each one has scored,
 * and the cell's grade book (`recordQueries.grades` keeps its key `['school', 'period-grades',
 * …]`, so a write that answers with the book replaces it in place).
 */
export const gradebookQueries = {
  evaluations: (api: ApiClient, cell: GradesCell) =>
    queryOptions({
      queryKey: ['school', 'evaluations', cell.groupId, cell.subjectId, cell.periodId],
      queryFn: () => api.request<EvaluationList>(`/evaluations${cellQuery(cell)}`),
      enabled: cell.groupId !== '' && cell.subjectId !== '' && cell.periodId !== '',
    }),
};
